import React, { useState, useEffect } from 'react';
import { getPublicCareers } from '../services/api';

const Career = ({ onApplyClick }) => {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      const response = await getPublicCareers();
      let items = [];
      
      // Handle different response formats
      if (response && response.data && Array.isArray(response.data)) {
        items = response.data;
      } else if (response && Array.isArray(response)) {
        items = response;
      } else if (response && response.success && response.data && Array.isArray(response.data)) {
        items = response.data;
      }
      
      // Sort by order
      items.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      setCareers(items);
    } catch (err) {
      console.error('Error fetching careers:', err);
      setError('Failed to load career opportunities');
    } finally {
      setLoading(false);
    }
  };

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `https://inteliheads.in${url}`;
    return url;
  };

  const getDepartments = () => {
    const departments = ['all', ...new Set(careers.map(career => career.department || career.categoryName || 'General'))];
    return departments;
  };

  const getDepartmentIcon = (department) => {
    const icons = {
      'Engineering': '💻',
      'Sales': '📈',
      'Marketing': '📢',
      'Support': '🎧',
      'General': '💼',
      'all': '📋'
    };
    return icons[department] || '💼';
  };

  const formatSalary = (salary) => {
    if (!salary || salary === 'Negotiable') return 'Negotiable';
    return salary;
  };

  const formatLocation = (location) => {
    if (!location) return 'Remote';
    return location;
  };

  const filteredCareers = selectedDepartment === 'all' 
    ? careers.filter(career => career.isActive !== false)
    : careers.filter(career => (career.department === selectedDepartment || career.categoryName === selectedDepartment) && career.isActive !== false);

  if (loading) {
    return (
      <section id="career" style={{ padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#2C7A7B' }}></i>
            <p style={{ marginTop: '1rem', color: '#5A6E7F' }}>Loading career opportunities...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="career" style={{ padding: '5rem 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#DC2626' }}></i>
            <p style={{ marginTop: '1rem', color: '#DC2626' }}>{error}</p>
          </div>
        </div>
      </section>
    );
  }

  // Fallback to static content if no careers in database
  if (careers.length === 0) {
    const fallbackJobs = [
      {
        icon: '💻',
        title: 'Senior Power Systems Engineer',
        requirements: 'PowerFactory, Python, grid code compliance.',
        location: 'Remote',
        type: 'Full-time'
      },
      {
        icon: '📚',
        title: 'Technical Training Manager',
        requirements: 'Curriculum design, MATLAB/Simulink expertise.',
        location: 'Hybrid',
        type: 'Full-time'
      },
      {
        icon: '👥',
        title: 'Business Development Lead',
        requirements: 'Enterprise sales, strategic partnerships.',
        location: 'Remote',
        type: 'Full-time'
      }
    ];

    return (
      <section id="career" style={{ padding: '5rem 0' }}>
        <div className="container">
          <h2 style={{ fontSize: '2.25rem', marginBottom: '1rem', color: '#0A192F', textAlign: 'center' }}>
            Join Our Global Team
          </h2>
          <div style={{ fontSize: '1.125rem', color: '#5A6E7F', textAlign: 'center', marginBottom: '3rem' }}>
            Shape the future of engineering simulation and power systems.
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {fallbackJobs.map((job, index) => (
              <div key={index} style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '24px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                border: '1px solid #E2E8F0',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.borderColor = '#2C7A7B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#E2E8F0';
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{job.icon}</div>
                <div style={{ marginBottom: '0.5rem' }}>
                  <span style={{
                    background: '#E3F2FD',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    display: 'inline-block'
                  }}>
                    {job.type}
                  </span>
                  <span style={{
                    background: '#E6F7F5',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    display: 'inline-block',
                    marginLeft: '0.5rem'
                  }}>
                    📍 {job.location}
                  </span>
                </div>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', color: '#0A192F' }}>{job.title}</h3>
                <p style={{ color: '#5A6E7F', lineHeight: '1.6', marginBottom: '1rem' }}>{job.requirements}</p>
                <button onClick={onApplyClick} className="btn-outline" style={{
                  border: '2px solid #2C7A7B',
                  color: '#2C7A7B',
                  background: 'transparent',
                  padding: '0.5rem 1.2rem',
                  borderRadius: '40px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%'
                }}>
                  Apply Now →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  const departments = getDepartments();

  return (
    <section id="career" style={{ padding: '5rem 0' }}>
      <div className="container">
        <h2 style={{ 
          fontSize: 'clamp(1.8rem, 5vw, 2.25rem)', 
          marginBottom: '0.5rem', 
          color: '#0A192F', 
          textAlign: 'center',
          fontWeight: 700
        }}>
          Join Our Global Team
        </h2>
        <div style={{ 
          fontSize: 'clamp(1rem, 4vw, 1.125rem)', 
          color: '#5A6E7F', 
          textAlign: 'center', 
          marginBottom: '2rem'
        }}>
          Shape the future of engineering simulation and power systems.
        </div>

        {/* Department Filter */}
        {departments.length > 2 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
            marginBottom: '2rem'
          }}>
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                style={{
                  padding: '0.5rem 1.2rem',
                  borderRadius: '40px',
                  border: '1px solid #E2E8F0',
                  background: selectedDepartment === dept ? '#2C7A7B' : 'white',
                  color: selectedDepartment === dept ? 'white' : '#5A6E7F',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
              >
                {getDepartmentIcon(dept)} {dept === 'all' ? 'All Departments' : dept}
              </button>
            ))}
          </div>
        )}

        {/* Stats */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <p style={{ color: '#5A6E7F' }}>
            <i className="fas fa-briefcase" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
            <strong>{filteredCareers.length}</strong> open position{filteredCareers.length !== 1 ? 's' : ''} available
          </p>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem'
        }}>
          {filteredCareers.map((career, index) => (
            <div 
              key={career._id || index}
              style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '20px',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                border: '1px solid #E2E8F0',
                transition: 'all 0.3s ease',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.borderColor = '#2C7A7B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = '#E2E8F0';
              }}
            >
              {/* Header with Icon and Badges */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ fontSize: '2.5rem' }}>
                  {career.imageUrl ? (
                    <img 
                      src={getFullImageUrl(career.imageUrl)} 
                      alt={career.title}
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = getDepartmentIcon(career.department);
                      }}
                    />
                  ) : (
                    getDepartmentIcon(career.department)
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={{
                    background: '#E3F2FD',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    color: '#2C7A7B',
                    fontWeight: 500
                  }}>
                    {career.type || 'Full-time'}
                  </span>
                  <span style={{
                    background: '#E6F7F5',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '20px',
                    fontSize: '0.7rem',
                    color: '#2C7A7B',
                    fontWeight: 500
                  }}>
                    📍 {formatLocation(career.location)}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h3 style={{ 
                fontSize: '1.2rem', 
                marginBottom: '0.5rem', 
                color: '#0A192F',
                fontWeight: 700
              }}>
                {career.title}
              </h3>

              {/* Department */}
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{
                  background: '#F7F9FC',
                  padding: '0.2rem 0.6rem',
                  borderRadius: '20px',
                  fontSize: '0.7rem',
                  color: '#5A6E7F'
                }}>
                  <i className="fas fa-building"></i> {career.department || career.categoryName || 'General'}
                </span>
              </div>

              {/* Experience & Salary */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid #E2E8F0'
              }}>
                <span style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>
                  <i className="fas fa-clock"></i> {career.experience || '2-5 years'}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#2C7A7B', fontWeight: 600 }}>
                  {formatSalary(career.salary)}
                </span>
              </div>

              {/* Description */}
              {career.description && (
                <p style={{
                  fontSize: '0.85rem',
                  color: '#5A6E7F',
                  lineHeight: '1.5',
                  marginBottom: '1rem',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {career.description}
                </p>
              )}

              {/* Requirements Preview */}
              {career.requirements && career.requirements.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#0A192F', marginBottom: '0.25rem' }}>
                    Key Requirements:
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {career.requirements.slice(0, 2).map((req, idx) => (
                      <li key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.75rem',
                        color: '#5A6E7F',
                        marginBottom: '0.25rem'
                      }}>
                        <i className="fas fa-check-circle" style={{ color: '#10B981', fontSize: '0.6rem' }}></i>
                        <span>{req.length > 50 ? `${req.substring(0, 50)}...` : req}</span>
                      </li>
                    ))}
                    {career.requirements.length > 2 && (
                      <li style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                        +{career.requirements.length - 2} more requirements
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Apply Button */}
              <button 
                onClick={() => onApplyClick(career)}
                className="btn-outline" 
                style={{
                  border: '2px solid #2C7A7B',
                  color: '#2C7A7B',
                  background: 'transparent',
                  padding: '0.6rem 1.2rem',
                  borderRadius: '40px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  width: '100%',
                  marginTop: 'auto',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#2C7A7B';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#2C7A7B';
                }}
              >
                Apply Now <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          ))}
        </div>

        {/* No Jobs Message */}
        {filteredCareers.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem', 
            background: '#F7F9FC', 
            borderRadius: '20px',
            border: '2px dashed #E2E8F0'
          }}>
            <i className="fas fa-briefcase" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }}></i>
            <p style={{ color: '#5A6E7F', marginBottom: '0.5rem' }}>No open positions at the moment.</p>
            <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>Please check back later for new opportunities.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Career;